import Line from './../2DObjects/Line.js';
import Text from './../2DObjects/Text.js';
import Button from './../2DObjects/Button.js';
import TabExplorer from './../2DObjects/TabExplorer.js';
import Picture from './../2DObjects/Picture.js';
import Lottie from './../2DObjects/Lottie.js';
import ImgComparer from './../2DObjects/ImgComparer.js';
import Iframe from './../2DObjects/Iframe.js';
import Video from './../2DObjects/Video.js';
import Slider from './../2DObjects/Slider.js';
import SliderScript from './../2DObjects/SliderScript.js';
import ImageCarousel from './../2DObjects/ImageCarousel.js';
import CircleSlider from './../2DObjects/CircleSlider.js';
import MathFormula from './../2DObjects/MathFormula.js';
import Wheel from './../2DObjects/Wheel.js';
import AtomUI from './../2DObjects/AtomUI.js';
import TerrainCarUI from './../2DObjects/TerrainCarUI.js';
import EmFieldUI from './../2DObjects/EmFieldUI.js';
import MeanMedianModeInfo from './../2DObjects/MeanMedianModeInfo.js';
import RollerCoaster from './../2DObjects/RollerCoaster.js';
import CylinderInfo from './../2DObjects/CylinderInfo.js';
import Feedback from './../2DObjects/Feedback.js';
import SlideManager from '../utils/slide-manager.js';
import Highlighter from '../2DObjects/highlighter.js';
import CooboLink from '../2DObjects/CooboLink.js';
import CustomizableRegion from '../2DObjects/customizable-region.js';
import Graphing from '../2DObjects/graphing.js';
import FlashCard from "../2DObjects/FlashCard";

const NSString = "http://www.w3.org/2000/svg";

class Layer {

    constructor(options = {}) {

        this.type = options.type || '2D';
        this.subtype = options.subtype;
        this.dontShowInHierarchyPanel = "dontShowInHierarchyPanel" in options
            ? options.dontShowInHierarchyPanel
            : false;
        this.isTemporary = "temporary" in options
            ? options.temporary
            : false;

        this.name = options.name || '';

        this.editorAnimationDelay = options.editorAnimationDelay || 0;

        this.isText = options.isText || false;

        this.level = 0;

        this.index = options.hasOwnProperty('index') ? options.index : 0;
        this.reordered = Boolean(options.reordered);

        this.uuid = options.uuid || THREE.Math.generateUUID();

        this._visible = options.visible !== false;
        this.isVisible = true;

        this.parent = options.parent;

        this.children = [];
        this.objects = [];
        /**
         * @type {HTMLElement}
         */
        this.domElement = null;

        this.styles = {
            position: 'absolute',
            left: '0',
            top: '0',
            width: '100%',
            height: "calc(100%)",
            overflow: "hidden",
            pointerEvents: 'none',
            zIndex: 5,
        };

        if (this.type === '2D') {
            SlideManager.currentSlide.layers2D[this.uuid] = 1;

            if (HTML_LAYERS.indexOf(this.subtype) !== -1 || this.isText) {
                //jquery ui slider is not working inside svg->foreignObject (problem with calculation of thumb position)
                //svg is not needed in this case div is enough
                this.domElement = document.createElement('div');
                this.domElement.id = this.uuid;
            } else {
                this.domElement = document.createElementNS(NSString, 'svg');
                this.domElement.id = this.uuid;

                //required for lines arrows at least
                this.defs = document.createElementNS(NSString, 'defs');
                this.domElement.appendChild(this.defs);

                let filter = document.createElementNS("http://www.w3.org/2000/svg", "filter");
                filter.setAttribute("id", "blur-filter");
                filter.setAttribute("x", "-2");
                filter.setAttribute("y", "-2");
                filter.setAttribute("width", "200");
                filter.setAttribute("height", "200");

                let gaussianFilter = document.createElementNS("http://www.w3.org/2000/svg", "feGaussianBlur");
                gaussianFilter.setAttribute("in", "SourceGraphic");
                gaussianFilter.setAttribute("stdDeviation", "2");

                filter.appendChild(gaussianFilter);
                this.defs.appendChild(filter);
            }
            Object.assign(this.domElement.style, this.styles);

            this.objects = this.initObjects(options.objects);
        } else if (this.type === 'Renderer') {
            this.domElement = options.domElement;

            Object.assign(this.domElement.style, {
                position: 'absolute',
                left: '0',
                top: '0',
                pointerEvents: 'none'
            });
        }

        this.domElement.setAttribute("type-2d", "Layer");

        return this;
    }

    add(child) {
        (this.type === '2D' ? this.objects : this.children)[child.index] = child;

        child.parent = this.uuid;

        return this;
    }

    clone() {
        const uuid = THREE.Math.generateUUID(),
            objectsWithNewUuids = {},
            objectsWithCurrentUuids = JSON.parse(JSON.stringify(Q3.data.layers.layers2D[this.uuid].objects));//deep cloning object

        for (let currentUuid in objectsWithCurrentUuids)
            objectsWithNewUuids[THREE.Math.generateUUID()] = objectsWithCurrentUuids[currentUuid];

        let addOffset = (objects, properties) => {
            for (let object of objects) {
                if (object) {
                    for (let property of properties) {
                        if (property in object) {
                            object[property] += 0.01;
                        }
                    }
                }
            }
        }

        let layerData = Q3.data.layers.layers2D[this.uuid];
        let properties = ['x', 'y', 'x1', 'y1', 'x2', 'y2'];
        for (let object of this.objects) {
            let objectData = layerData.objects[object.uuid];

            let objects = [object, objectData, object.sizeSm, objectData.sizeSm, object.sizeLg, objectData.sizeLg];
            addOffset(objects, properties);

            if (object.redraw) {
                object.redraw();
            }
        }

        let data = {
            uuid: uuid,
            name: this.name.replace(/\(copy\)/g, '') + "(copy)",
            type: this.type,
            subtype: this.subtype,
            isText: this.isText,
            parent: this.parent,
            reordered: this.reordered,
            children: this.children,
            objects: objectsWithNewUuids,
            visible: this.visible
        };

        Q3.data.layers.layers2D[uuid] = {
            name: data.name,
            type: data.type,
            subtype: data.subtype,
            isText: data.isText,
            reordered: data.reordered,
            children: data.children,
            objects: objectsWithNewUuids,
            visible: data.visible,
        };

        const l = new Layer(data);

        const brothers = this.parent ? Q3.layers.list[this.parent].children : Q3.layers.graph;

        brothers.splice(this.index, 0, l);

        return l;
    }

    remove(child) {
        const a = (this.type === '2D' ? this.objects : this.children);

        a.splice(a.indexOf(child), 1);

        //update indices of next children
        for (let i = child.index; i < a.length; i++) a[i].index = i;

        child.parent = null;

        return this;
    }

    delete() {
        this.children.forEach(child => child.delete());
        this.objects.forEach(child => child.delete());

        if (this.domElement.parentElement)
            this.domElement.remove();

        delete Q3.layers.list[this.uuid];
    }

    initObjects(objects = {}) {
        return Object.keys(objects)
            .sort((a, b) => {
                if (objects[a].index < objects[b].index) { return -1 }
                else if (objects[a].index > objects[b].index) { return 1 }

                return 0;
            })
            .map(uuid => {
                const data = objects[uuid];

                let objMap = {
                    "line": Line,
                    "text": Text,
                    "flashcard": FlashCard,
                    "button": Button,
                    "tabexplorer": TabExplorer,
                    "picture": Picture,
                    "lottie": Lottie,
                    "imgComparer": ImgComparer,
                    "iframe": Iframe,
                    "video": Video,
                    "circleSlider": CircleSlider,
                    "slider": Slider,
                    "sliderScript": SliderScript,
                    "imageCarousel": ImageCarousel,
                    "mathFormula": MathFormula,
                    "wheel": Wheel,
                    "atomUi": AtomUI,
                    "terrainCarUi": TerrainCarUI,
                    "emFieldUi": EmFieldUI,
                    "mmmInfo": MeanMedianModeInfo,
                    "cylinderInfo": CylinderInfo,
                    "feedback": Feedback,
                    "rollercoaster": RollerCoaster,
                    "highlighter": Highlighter,
                    "coobolink": CooboLink,
                    "customizable-region": CustomizableRegion,
                    "graphing": Graphing
                };

                if (data.type === "text") {
                    data.contentConverted = true;
                }

                return new objMap[data.type](data, this, uuid);
            });
    }

    get visible() {
        return this._visible;
    }

    set visible(val) {
        this._visible = val;
        for (let o of this.objects) {
            typeof o.onVisibilityChange === "function" && o.onVisibilityChange(val);
        }
    }

    forceUpdate() {
        this.objects.forEach(obj => obj.needsUpdate = true);
    }

    render(width, height) {

        if (!Q3.isReady || this.type === 'Renderer') return;

        if (this.type === '2DGroup') return this.children.forEach(c => c.render(width, height));

        for (let o of this.objects) {
            let scale = o instanceof Line ? Q3.RenderManager.pixelRatio : 1;
            o.render(width * scale, height * scale);
        }
    }
}

export default Layer;
export { Layer };

const HTML_LAYERS = Object.freeze([
    'sliders', 'iframes', 'videos', 'flashcard', 'imgComparer',
    'atomUi', 'emFieldUi', 'wheel', 'rollercoasters',
    'rollercoasters', 'mmmInfo', "highlighters",
    'cylinderInfo', 'terrainCarUi', 'feedback', 'button', "picture",
    "lottie", 'tabexplorer', 'coobolink', 'carousel'
]);